import { AxiosRequestConfig, AxiosResponse } from 'axios';
import fdcpApi from 'utils/fdcpApi';
import { FdcpCity, FdcpSeason } from './fdcp-types';

export type ResultsPage<T> = {
    ContinuationToken: string | null;
    ContinuationHash: string | null;
    Results: T[];
};

/**
 * Enumerates all result pages in a pagined FDCP request.
 * The implementation is based on:
 * https://fifadigital.atlassian.net/wiki/spaces/FDCP/pages/125775999/FDCP+Pagination
 */
async function GetPagedResults<T>(endpoint: string, queryParams: URLSearchParams): Promise<Array<T>> {
    const results: Array<T> = [];

    let resultsPage: ResultsPage<T> | undefined = undefined;
    do {
        const continuationHash = resultsPage?.ContinuationHash;
        const continuationToken = resultsPage?.ContinuationToken;

        let requestConfig: AxiosRequestConfig | undefined = undefined;
        if (continuationHash && continuationToken) {
            queryParams.set('continuationhash', continuationHash);
            requestConfig = {
                headers: {
                    'x-mdp-continuation-token': continuationToken,
                },
            };
        }
        const fdcpEndpoint = `${endpoint}?${queryParams.toString()}`;
        const axiosResponse: AxiosResponse<any> = await fdcpApi.get<ResultsPage<T>>(fdcpEndpoint, requestConfig);
        if (!axiosResponse.data?.Results) break;

        results.push(...axiosResponse.data.Results);
        resultsPage = axiosResponse.data;
    } while (resultsPage?.Results.length && resultsPage?.ContinuationToken);

    return results;
}

export type SearchSeasonByNameQuery = {
    searchString: string;
    language?: string;
    count?: number;
};

async function searchSeasonByName({ searchString, language = 'en', count = 500 }: SearchSeasonByNameQuery) {
    if (!searchString) return Array<FdcpSeason>();
    const queryParams = new URLSearchParams([
        ['name', searchString],
        ['language', language],
        ['count', `${count}`],
    ]);
    const response = await GetPagedResults<FdcpSeason>(`/seasons/search`, queryParams);
    return response;
}

type SearchCityByNameQueryParams = {
    searchString: string;
    language?: string;
    count?: number;
};

async function searchCityByName({ searchString, language = 'en', count = 50 }: SearchCityByNameQueryParams) {
    if (!searchString) return Array<FdcpCity>();
    const queryParams = new URLSearchParams([
        ['name', searchString],
        ['language', language],
        ['count', `${count}`],
    ]);
    const response = await GetPagedResults<FdcpCity>('/cities/search', queryParams);
    return response;
}

const FDCPServices = {
    searchSeasonByName,
    searchCityByName,
};

export default FDCPServices;
